import { FirebaseError } from 'firebase/app';

/** The providers whose OAuth login returns to `/integrations/link-complete/:provider`. */
export const linkProviders = ['bexio', 'gsaktiv'] as const;

export type LinkProvider = (typeof linkProviders)[number];

/** How completing a link ended, as the link-complete page reports it. */
export type LinkCompletionOutcome = 'linked' | 'wrong-user' | 'expired' | 'failed' | 'invalid-callback';

/** Finishes the link on the backend with the provider's callback query; rejects with the callable's error. */
export type CompleteLink = (callbackQuery: string) => Promise<void>;

const linkCompletePath = /^\/integrations\/link-complete\/([^/]+)\/?$/;

export function isLinkProvider(value: unknown): value is LinkProvider {
  return linkProviders.some((provider) => provider === value);
}

/** The provider whose link-complete page the path names, or undefined for any other path. Case-sensitive. */
export function linkProviderOfPath(pathname: string): LinkProvider | undefined {
  const provider = linkCompletePath.exec(pathname)?.[1];
  return isLinkProvider(provider) ? provider : undefined;
}

/**
 * The provider's callback query carried by a URL fragment, without the leading '#'.
 * The query is returned verbatim. It is undefined when it lacks a non-empty `code` or `state`,
 * because the backend cannot complete a link without them.
 */
export function callbackQueryOfFragment(fragment: string): string | undefined {
  const callbackQuery = fragment.startsWith('#') ? fragment.slice(1) : fragment;
  const params = new URLSearchParams(callbackQuery);
  return params.get('code') && params.get('state') ? callbackQuery : undefined;
}

/** Maps a rejection of the completeLink callable to the outcome the page reports. */
export function linkCompletionFailure(error: unknown): Exclude<LinkCompletionOutcome, 'linked' | 'invalid-callback'> {
  if (!(error instanceof FirebaseError)) return 'failed';
  switch (error.code) {
    case 'functions/permission-denied':
      return 'wrong-user';
    case 'functions/deadline-exceeded':
    case 'functions/not-found':
      return 'expired';
    default:
      return 'failed';
  }
}

/**
 * Holds the provider callback of one page load, from the URL to the backend.
 * `capture` takes the one-time callback query off the address bar, `complete` hands it to the backend once.
 */
export function createLinkCompletion() {
  const capturedFragments = new Map<LinkProvider, string>();
  const outcomes = new Map<LinkProvider, Promise<LinkCompletionOutcome>>();

  /**
   * Keeps the fragment of a link-complete URL for `complete` and removes it from the current history entry.
   * Any other URL is left untouched.
   */
  function capture(
    location: Pick<Location, 'pathname' | 'search' | 'hash'>,
    history: Pick<History, 'state' | 'replaceState'>,
  ): void {
    const provider = linkProviderOfPath(location.pathname);
    if (!provider || !location.hash) return;

    capturedFragments.set(provider, location.hash);
    history.replaceState(history.state, '', location.pathname + location.search);
  }

  /**
   * Completes the provider's link with the captured callback query.
   * `completeLink` runs at most once per provider; every later call returns the first call's outcome.
   * Without a usable callback query the outcome is 'invalid-callback' and `completeLink` is not called.
   */
  function complete(provider: LinkProvider, completeLink: CompleteLink): Promise<LinkCompletionOutcome> {
    const known = outcomes.get(provider);
    if (known) return known;

    const callbackQuery = callbackQueryOfFragment(capturedFragments.get(provider) ?? '');
    capturedFragments.delete(provider);

    const outcome =
      callbackQuery === undefined
        ? Promise.resolve<LinkCompletionOutcome>('invalid-callback')
        : outcomeOf(completeLink, callbackQuery);
    outcomes.set(provider, outcome);
    return outcome;
  }

  return { capture, complete };
}

async function outcomeOf(completeLink: CompleteLink, callbackQuery: string): Promise<LinkCompletionOutcome> {
  try {
    await completeLink(callbackQuery);
    return 'linked';
  } catch (error) {
    console.error('Failed to complete the link', error);
    return linkCompletionFailure(error);
  }
}

/** The instance of this page load: the router captures the callback, the link-complete page completes it. */
export const linkCompletion = createLinkCompletion();
